import axios from 'axios';
import authService from './authService';

const DIRECTUS_URL = 'http://localhost/cms';

class LikeService {
  // Like a post
  async likePost(postId, userId) {
    try {
      const token = authService.getAccessToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      // Ensure postId is an integer
      const postIdInt = parseInt(postId, 10);
      if (isNaN(postIdInt)) {
        throw new Error('Invalid post ID');
      }

      const response = await axios.post(
        `${DIRECTUS_URL}/items/post_likes`,
        {
          user: userId,
          post: postIdInt,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return {
        success: true,
        likeId: response.data.data.id,
      };
    } catch (error) {
      console.error('Like post error:', error);

      // Handle duplicate like attempt
      if (error.response?.status === 400 || error.response?.status === 409) {
        const errorMsg = error.response?.data?.errors?.[0]?.message || '';
        if (errorMsg.includes('duplicate') || errorMsg.includes('unique')) {
          throw new Error('You have already liked this post');
        }
      }

      throw new Error(error.response?.data?.errors?.[0]?.message || 'Failed to like post');
    }
  }

  // Unlike a post
  async unlikePost(likeId) {
    try {
      const token = authService.getAccessToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      await axios.delete(`${DIRECTUS_URL}/items/post_likes/${likeId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
      };
    } catch (error) {
      console.error('Unlike post error:', error);
      throw new Error(error.response?.data?.errors?.[0]?.message || 'Failed to unlike post');
    }
  }

  // Get like count for a post
  async getLikeCount(postId) {
    try {
      const response = await axios.get(
        `${DIRECTUS_URL}/items/post_likes?filter[post][_eq]=${postId}&aggregate[count]=id`
      );

      return response.data.data?.[0]?.count?.id || 0;
    } catch (error) {
      console.error('Get like count error:', error);
      return 0;
    }
  }

  // Check if user has liked a post
  async checkIfLiked(postId, userId) {
    try {
      if (!userId) {
        return { isLiked: false, likeId: null };
      }

      const token = authService.getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.get(
        `${DIRECTUS_URL}/items/post_likes?filter[post][_eq]=${postId}&filter[user][_eq]=${userId}&limit=1`,
        { headers }
      );

      const likes = response.data.data;
      if (likes && likes.length > 0) {
        return {
          isLiked: true,
          likeId: likes[0].id,
        };
      }

      return { isLiked: false, likeId: null };
    } catch (error) {
      console.error('Check if liked error:', error);
      return { isLiked: false, likeId: null };
    }
  }

  // Get user's liked posts
  async getUserLikedPosts(userId) {
    try {
      const token = authService.getAccessToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await axios.get(
        `${DIRECTUS_URL}/items/post_likes?filter[user][_eq]=${userId}&fields=id,date_created,post.id,post.title,post.slug,post.excerpt,post.hero_image,post.status,post.date_created&sort=-date_created`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data.data || [];
    } catch (error) {
      console.error('Get user liked posts error:', error);
      throw new Error(error.response?.data?.errors?.[0]?.message || 'Failed to fetch liked posts');
    }
  }

  // Get posts with like data for a specific user
  async getPostsWithLikeData(userId = null) {
    try {
      let url = `${DIRECTUS_URL}/items/posts?fields=*,category.name,post_likes.count`;

      // If user is authenticated, also get their like status
      if (userId) {
        url += `,post_likes.id,post_likes.user`;
      }

      const token = authService.getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.get(url, { headers });
      const posts = response.data.data || [];

      // Process posts to add like information
      return posts.map(post => {
        const likeCount = post.post_likes?.length || 0;
        let isLiked = false;
        let likeId = null;

        if (userId && post.post_likes) {
          const userLike = post.post_likes.find(like => like.user === userId);
          if (userLike) {
            isLiked = true;
            likeId = userLike.id;
          }
        }

        return {
          ...post,
          likeCount,
          isLiked,
          likeId,
        };
      });
    } catch (error) {
      console.error('Get posts with like data error:', error);
      throw new Error('Failed to fetch posts');
    }
  }

  // Format like count for display (e.g., 1.2K)
  formatLikeCount(count) {
    if (count === 0) return '';
    if (count < 1000) return count.toString();
    if (count < 1000000) return (count / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    return (count / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
}

export default new LikeService();
